// NAS 実行明細のサイドパネル。
// 「明細を見る / View details」から必ず観測可能な反応が出るようにする（ボタンを死なせない）。
// 表示するのは相対パスと集計のみ。トークン・絶対パス・内部URL・本文は扱わない。
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { CircleNotch, Warning, ArrowsClockwise, StopCircle } from '@phosphor-icons/react';
import { derivePreviewState, deriveCrawlState, shortId } from './nasJobState';
import { NasRootCard } from './NasRootCard';
import { NasExtensionSummary } from './NasExtensionSummary';

// 大規模プレビューでも DOM を有界に保つ（行数を無制限に描画しない）。
const MAX_ROOT_CARDS = 50;
const EVENT_PAGE = 50;
const MAX_EVENT_ROWS = 200;

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const tok = localStorage.getItem('token');
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const CONTEXT_TITLE = {
  current_preview: ['実行中のプレビュー', 'Current preview'],
  latest_preview: ['直近のプレビュー', 'Latest preview'],
  historical_preview: ['過去のプレビュー', 'Historical preview'],
  historical_crawl: ['過去のクロール', 'Historical crawl'],
};

const PREVIEW_MODE_LABEL = {
  full_validation: ['完全検証プレビュー', 'Full validation preview'],
  quick_estimate: ['簡易見積り', 'Quick estimate'],
};

const COUNTER_LABEL = [
  ['files_observed', '検出', 'Observed'],
  ['eligible_files', '対象', 'Eligible'],
  ['new_files', '新規', 'New'],
  ['changed_files', '変更あり', 'Changed'],
  ['unchanged_files', '変更なし', 'Unchanged'],
  ['unsupported_extensions', '対象外の拡張子', 'Unsupported'],
  ['oversized_files', 'サイズ超過', 'Oversized'],
  ['hidden_files', '隠しファイル', 'Hidden'],
  ['guarded_files', '機密除外', 'Guarded'],
  ['other_skips', 'その他スキップ', 'Other skips'],
  ['failures', 'エラー', 'Failures'],
];

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('ja-JP') : '—');
const fmtSec = (s) => {
  if (s == null) return '—';
  const n = Math.max(0, Math.floor(s));
  return n >= 60 ? `${Math.floor(n / 60)}分${String(n % 60).padStart(2, '0')}秒` : `${n}秒`;
};

export function NasRunDetailSheet({ open, onOpenChange, target, returnFocusRef, onCancelled }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const kind = target?.kind;
  const id = target?.id;
  const [titleJa, titleEn] = CONTEXT_TITLE[target?.context] || ['実行明細', 'Run details'];

  const load = useCallback(async () => {
    if (!open || !id) return;
    setLoading(true); setError(null);
    try {
      if (kind === 'preview') {
        const [j, e] = await Promise.all([
          axios.get(`${API}/api/crawler/nas/preview/${id}`, { headers: authHeader() }),
          axios.get(`${API}/api/crawler/nas/preview/${id}/events`,
            { params: { limit: EVENT_PAGE }, headers: authHeader() }),
        ]);
        setJob(j.data); setEvents(e.data);
      } else {
        const [j, e] = await Promise.all([
          axios.get(`${API}/api/crawler/${id}`, { headers: authHeader() }),
          axios.get(`${API}/api/crawler/nas/runs/${id}/events`, { headers: authHeader() }),
        ]);
        setJob(j.data); setEvents(e.data);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError({
        status: e?.response?.status || 0,
        category: (d && typeof d === 'object' ? d.category : null) || 'request_failed',
        message: (d && typeof d === 'object' ? d.message : d) || '明細を取得できませんでした',
      });
      setJob(null); setEvents(null);
    } finally { setLoading(false); }
  }, [open, id, kind]);

  const loadMoreEvents = async () => {
    const rows = events?.events || [];
    if (kind !== 'preview' || !rows.length) return;
    setMoreLoading(true);
    try {
      const r = await axios.get(`${API}/api/crawler/nas/preview/${id}/events`, {
        params: { limit: EVENT_PAGE, after_seq: rows[rows.length - 1].seq },
        headers: authHeader() });
      setEvents((prev) => ({
        ...r.data,
        events: [...(prev?.events || []), ...(r.data?.events || [])].slice(0, MAX_EVENT_ROWS),
      }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError({ status: e?.response?.status || 0, category: 'events_page_failed',
        message: (d && typeof d === 'object' ? d.message : d) || 'イベントの追加取得に失敗しました' });
    } finally { setMoreLoading(false); }
  };

  useEffect(() => { load(); }, [load]);

  // 実行中のプレビューはパネルを開いている間だけ追従する（終端で停止）。
  useEffect(() => {
    if (!open || kind !== 'preview' || !job?.is_active) return undefined;
    const t = setInterval(load, (job?.poll_interval_seconds || 3) * 1000);
    return () => clearInterval(t);
  }, [open, kind, job?.is_active, job?.poll_interval_seconds, load]);

  const cancelPreview = async () => {
    setCancelling(true);
    try {
      const r = await axios.post(`${API}/api/crawler/nas/preview/${id}/cancel`, {},
        { headers: authHeader() });
      setJob(r.data?.job || job);
      onCancelled?.(r.data?.job);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError({ status: e?.response?.status || 0, category: 'cancel_failed',
        message: (d && typeof d === 'object' ? d.message : d) || '中止要求に失敗しました' });
    } finally { setCancelling(false); }
  };

  const st = kind === 'preview' ? derivePreviewState(job) : deriveCrawlState(job);
  const legacyNoEvents = !!job && events && events.retained === false;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right"
        className="w-full sm:max-w-xl overflow-y-auto bg-white"
        onCloseAutoFocus={(e) => { e.preventDefault(); returnFocusRef?.current?.focus(); }}
        data-testid="nas-detail-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="text-base" data-testid="nas-detail-title">
            {titleJa}
            <span className="block text-[11px] font-normal text-zinc-500">{titleEn}</span>
          </SheetTitle>
          <SheetDescription className="text-[11.5px]">
            実行ID <span className="font-mono" data-testid="nas-detail-job-id">{shortId(id)}</span>
            {' · '}{kind === 'preview' ? 'プレビュー / Preview' : 'クロール / Crawl'}
          </SheetDescription>
        </SheetHeader>

        {loading && !job && (
          <div className="py-10 flex items-center gap-2 text-[13px] text-zinc-500"
            data-testid="nas-detail-loading">
            <CircleNotch size={16} className="animate-spin" /> 読み込み中… / Loading…
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12px] text-rose-900"
            data-testid="nas-detail-error">
            <p className="font-semibold flex items-center gap-1.5">
              <Warning size={14} weight="fill" /> {error.message}
            </p>
            <p className="mt-0.5 text-[11px] opacity-80">
              分類 / type: <span className="font-mono">{error.category}</span>
              {error.status ? ` · HTTP ${error.status}` : ''}
            </p>
            <Button size="sm" variant="outline" onClick={load}
              className="mt-2 h-7 text-[11px]" data-testid="nas-detail-retry">
              <ArrowsClockwise size={12} className="mr-1" /> 再取得 / Retry
            </Button>
          </div>
        )}

        {job && (
          <div className="mt-4 space-y-4">
            <div className={`rounded-lg border ${st.border} ${st.bg} px-3 py-2.5`}
              data-testid="nas-detail-status" data-state={st.key}>
              <div className="flex items-center gap-2">
                <st.Icon size={16} className={`${st.color} ${st.spin ? 'animate-spin' : ''}`} />
                <p className={`text-[13px] font-bold ${st.color}`}>{st.labelJa}</p>
                <span className="text-[11px] text-zinc-500">{st.labelEn}</span>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-zinc-600">
                <span>開始 / Started: {fmtDT(job.started_at)}</span>
                <span>終了 / Finished: {fmtDT(job.completed_at || job.finished_at)}</span>
                {kind === 'preview' && <span>経過 / Elapsed: {fmtSec(job.elapsed_seconds)}</span>}
                <span>実行モード / Mode: <b>{job.crawler_mode || '—'}</b></span>
                {job.agent_version && <span>エージェント / Agent: v{job.agent_version}</span>}
              </div>
              {kind === 'preview' && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5"
                  data-testid="nas-detail-preview-mode" data-preview-mode={job.preview_mode || ''}>
                  <span className={`text-[10.5px] px-2 py-0.5 rounded-full border font-semibold ${
                    job.preview_mode === 'full_validation'
                      ? 'border-teal-300 bg-teal-50 text-teal-800'
                      : 'border-zinc-300 bg-white text-zinc-700'}`}>
                    {(PREVIEW_MODE_LABEL[job.preview_mode] || [job.preview_mode, ''])[0]}
                    {' / '}{(PREVIEW_MODE_LABEL[job.preview_mode] || ['', job.preview_mode])[1]}
                  </span>
                  {job.pagination_used && (
                    <span className="text-[10.5px] text-zinc-600">
                      ページ {job.aggregate?.pages ?? 0} · 1 ページ {job.page_size || '—'} 件
                    </span>
                  )}
                  <span className={`text-[10.5px] px-2 py-0.5 rounded-full border ${
                    job.authorises_ingestion
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                      : 'border-zinc-300 bg-white text-zinc-600'}`}
                    data-testid="nas-detail-authorises">
                    {job.authorises_ingestion
                      ? '取り込みを許可できます / Can authorise ingestion'
                      : '取り込みは許可されません / Does not authorise ingestion'}
                  </span>
                </div>
              )}
              {kind === 'preview' && (
                <p className="mt-1.5 text-[11.5px] font-semibold text-zinc-700"
                  data-testid="nas-detail-progress">
                  クロール対象パス {job.aggregate?.roots_completed || 0} / {job.total_roots || 0} 完了
                  {job.current_root && job.is_active && (
                    <span className="ml-1 font-mono font-normal">（現在: {job.current_root}）</span>
                  )}
                </p>
              )}
              {job.truncated && (
                <p className="mt-1.5 text-[11.5px] font-semibold text-amber-800"
                  data-testid="nas-detail-truncated">
                  {job.truncation_message_ja || '上限に達したため結果は不完全です'}
                  （<span className="font-mono">{job.truncation_reason}</span>）
                  <span className="block font-normal opacity-80">
                    {job.truncation_message_en || 'Results are incomplete — a limit was reached.'}
                  </span>
                </p>
              )}
              {(job.typed_error || job.error) && (
                <p className="mt-1.5 text-[11.5px] text-rose-800" data-testid="nas-detail-typed-error">
                  {job.typed_error_message_ja || job.error}
                  {job.typed_error && <span className="ml-1 font-mono opacity-80">({job.typed_error})</span>}
                  {job.typed_error_message_en && (
                    <span className="block opacity-80">{job.typed_error_message_en}</span>
                  )}
                </p>
              )}
              {kind === 'preview' && job.is_active && (
                <Button size="sm" variant="outline" onClick={cancelPreview} disabled={cancelling}
                  className="mt-2 h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                  data-testid="nas-detail-cancel-preview">
                  <StopCircle size={12} className="mr-1" />
                  {cancelling ? '要求中…' : 'プレビューを中止 / Cancel preview'}
                </Button>
              )}
              {kind === 'preview' && job.cancellation_requested && job.is_active && (
                <p className="mt-1 text-[11px] text-zinc-600" data-testid="nas-detail-cancel-pending">
                  中止を要求しました。実行中の列挙が終わり次第停止します。
                  <span className="block opacity-80">Cancellation requested; it stops after the current listing.</span>
                </p>
              )}
            </div>

            <div>
              <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">
                集計 <span className="text-[10.5px] font-normal text-zinc-400">Aggregate</span>
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="nas-detail-aggregate">
                {kind === 'preview'
                  ? COUNTER_LABEL.map(([k, ja, en]) => (
                    <div key={k} className="rounded border border-zinc-200 px-2 py-1.5 bg-white">
                      <p className="text-[10.5px] text-zinc-500">{ja} <span className="opacity-70">{en}</span></p>
                      <p className={`text-[13px] font-bold tabular-nums ${k === 'failures' && (job.aggregate?.[k] || 0) > 0 ? 'text-rose-700' : 'text-zinc-800'}`}>
                        {job.aggregate?.[k] ?? 0}
                      </p>
                    </div>
                  ))
                  : [['indexed', 'インデックス化', 'Indexed'], ['embedded', '意味検索の準備', 'Embedded'],
                    ['unchanged', '変更なし', 'Unchanged'], ['skipped', 'スキップ', 'Skipped'],
                    ['failed', '失敗', 'Failed']].map(([k, ja, en]) => (
                    <div key={k} className="rounded border border-zinc-200 px-2 py-1.5 bg-white">
                      <p className="text-[10.5px] text-zinc-500">{ja} <span className="opacity-70">{en}</span></p>
                      <p className={`text-[13px] font-bold tabular-nums ${k === 'failed' && (job.stats?.[k] || 0) > 0 ? 'text-rose-700' : 'text-zinc-800'}`}>
                        {job.stats?.[k] ?? 0}
                      </p>
                    </div>
                  ))}
              </div>
            </div>

            {kind === 'preview' && (job.roots || []).length > 0 && (
              <div>
                <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">
                  クロール対象パスごとの結果
                  <span className="text-[10.5px] font-normal text-zinc-400 ml-1">Per configured root</span>
                  <span className="ml-1 text-[10.5px] font-normal text-zinc-400">
                    — 既定は折りたたみ / collapsed by default
                  </span>
                </p>
                <div className="space-y-1.5">
                  {(job.roots || []).slice(0, MAX_ROOT_CARDS).map((r, i) => (
                    <NasRootCard key={r.path || i} root={r} index={i}
                      sampleLimit={job.limits?.max_samples_per_root || 25} />
                  ))}
                </div>
                {(job.roots || []).length > MAX_ROOT_CARDS && (
                  <p className="mt-1 text-[10.5px] text-zinc-500" data-testid="nas-detail-roots-capped">
                    {MAX_ROOT_CARDS} / {job.roots.length} 件のクロール対象パスを表示しています。
                    <span className="block">Showing the first {MAX_ROOT_CARDS} configured roots.</span>
                  </p>
                )}
              </div>
            )}

            {kind === 'preview' && <NasExtensionSummary aggregate={job.aggregate} />}

            {kind === 'crawl' && (job.paths_config || []).length > 0 && (
              <div>
                <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">
                  クロール対象パス <span className="text-[10.5px] font-normal text-zinc-400">Configured roots</span>
                </p>
                <ul className="space-y-0.5">
                  {job.paths_config.map((p, i) => (
                    <li key={i} className="font-mono text-[11.5px] text-zinc-700"
                      data-testid={`nas-detail-crawl-root-${i}`}>{p.prefix}</li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">
                明細イベント <span className="text-[10.5px] font-normal text-zinc-400">Detail events</span>
              </p>
              {legacyNoEvents ? (
                <div className="rounded border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-[12px] text-zinc-700"
                  data-testid="nas-detail-legacy-empty">
                  この過去の実行には詳細イベントが保存されていません。
                  <span className="block text-[11px] text-zinc-500 mt-0.5">
                    Detailed events were not retained for this historical run.
                  </span>
                </div>
              ) : (events?.events || []).length === 0 ? (
                <p className="text-[12px] text-zinc-500" data-testid="nas-detail-events-empty">
                  イベントはまだありません。 / No events yet.
                </p>
              ) : (
                <>
                  <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded max-h-64 overflow-y-auto"
                    data-testid="nas-detail-events">
                    {(events.events || []).slice(0, MAX_EVENT_ROWS).map((e, i) => (
                      <li key={e.id || i} className="px-2 py-1 text-[11px] flex items-start gap-2">
                        <span className="font-mono text-zinc-400 shrink-0">{e.seq ?? i + 1}</span>
                        <span className="font-semibold text-zinc-600 shrink-0">
                          {e.type || e.status_label_ja || e.status}
                        </span>
                        <span className="text-zinc-700 min-w-0 break-all">
                          {e.message_ja || e.path || e.reason_label}
                          {e.reason_label && e.path ? ` — ${e.reason_label}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {kind === 'preview' && events.has_more
                    && (events.events || []).length < MAX_EVENT_ROWS && (
                    <Button size="sm" variant="outline" onClick={loadMoreEvents} disabled={moreLoading}
                      className="mt-1.5 h-7 text-[11px]" data-testid="nas-detail-events-more">
                      {moreLoading ? '読み込み中…' : `さらに ${EVENT_PAGE} 件読み込む / Load more`}
                    </Button>
                  )}
                  {(events.events || []).length >= MAX_EVENT_ROWS && (
                    <p className="mt-1 text-[10.5px] text-zinc-500" data-testid="nas-detail-events-capped">
                      表示は {MAX_EVENT_ROWS} 件までです（集計は影響を受けません）。
                      <span className="block">Display is capped at {MAX_EVENT_ROWS} rows; counts are unaffected.</span>
                    </p>
                  )}
                </>
              )}
              {events && events.total > (events.events || []).length && (
                <p className="mt-1 text-[10.5px] text-zinc-500" data-testid="nas-detail-events-count">
                  {(events.events || []).length} / {events.total} 件を表示（保存上限 {
                    events.max_events || events.event_cap}）
                  <span className="block">
                    Showing {(events.events || []).length} of {events.total} stored events.
                  </span>
                </p>
              )}
            </div>

            {kind === 'preview' && (
              <p className="text-[10.5px] text-zinc-400">
                プレビューはインデックスを作成しません（検索索引・埋め込み・カタログ・NAS への書き込みなし）。
                <span className="block">A preview never indexes: no search index, embedding, catalogue or NAS writes.</span>
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default NasRunDetailSheet;
